import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import {
  ArrowLeft, CreditCard, CheckCircle, XCircle, AlertTriangle,
  RefreshCw, Loader2, ExternalLink, Copy,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminStripePriceCheckPage = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchPrices = useCallback(async () => {
    setRefreshing(true);
    try {
      const res = await fetch(`${API_URL}/api/subscriptions/admin/stripe-price-check`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = await res.json();
      if (!res.ok) {
        toast.error(json.detail || 'Failed to load Stripe prices');
        setData(null);
      } else {
        setData(json);
      }
    } catch (err) {
      toast.error('Network error loading Stripe prices');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [token]);

  useEffect(() => {
    fetchPrices();
  }, [fetchPrices]);

  const copyToClipboard = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied`);
    } catch {
      toast.error('Failed to copy');
    }
  };

  const formatPrice = (amount, interval) => {
    if (amount === null || amount === undefined) return '—';
    const intervalLabel = interval === 'monthly' || interval === 'month' ? '/mo'
      : interval === 'yearly' || interval === 'year' ? '/yr'
      : '';
    return `$${Number(amount).toFixed(2)}${intervalLabel}`;
  };

  return (
    <div className="space-y-6 max-w-6xl mx-auto p-4" data-testid="admin-stripe-price-check-page">
      <Button
        onClick={() => navigate('/admin/promo-codes')}
        variant="ghost"
        className="text-primary hover:text-primary/80"
        data-testid="back-to-admin-btn"
      >
        <ArrowLeft className="w-5 h-5 mr-2" />
        Back to Admin Panel
      </Button>

      <div className="bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 rounded-xl p-8 border border-indigo-100">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-indigo-400 to-purple-500 flex items-center justify-center shadow-md">
              <CreditCard className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-nunito font-bold text-[#2D2D2D]">
                Stripe Price Check
              </h1>
              <p className="text-[#57534E] mt-1">
                Verify your Stripe Price objects match the prices displayed on the landing page
              </p>
            </div>
          </div>
          <Button
            onClick={fetchPrices}
            disabled={refreshing}
            variant="outline"
            className="border-indigo-300"
            data-testid="refresh-prices-btn"
          >
            {refreshing ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4 mr-2" />
            )}
            Refresh
          </Button>
        </div>
      </div>

      {/* Status banner */}
      {data && (
        <Card
          className={`p-5 ${
            data.all_match
              ? 'bg-emerald-50 border-emerald-200'
              : 'bg-amber-50 border-amber-300'
          }`}
          data-testid="status-banner"
        >
          <div className="flex items-start gap-3">
            {data.all_match ? (
              <CheckCircle className="w-6 h-6 text-emerald-600 flex-shrink-0" />
            ) : (
              <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0" />
            )}
            <div className="flex-1">
              <h3 className={`font-bold ${data.all_match ? 'text-emerald-900' : 'text-amber-900'}`}>
                {data.all_match
                  ? 'All prices match — you are good to go!'
                  : data.any_error
                    ? 'One or more Stripe lookups failed — check the rows below'
                    : 'Mismatches detected — review the rows highlighted in amber'}
              </h3>
              <p className={`text-sm mt-1 ${data.all_match ? 'text-emerald-800' : 'text-amber-800'}`}>
                Stripe mode: <strong className="uppercase">{data.stripe_mode}</strong>
                {' · '}Checked at {new Date(data.checked_at).toLocaleString()}
              </p>
            </div>
          </div>
        </Card>
      )}

      {/* Price comparison table */}
      {loading ? (
        <Card className="p-12 text-center">
          <Loader2 className="w-8 h-8 text-indigo-400 mx-auto animate-spin" />
          <p className="text-[#57534E] mt-3">Pinging Stripe…</p>
        </Card>
      ) : !data ? (
        <Card className="p-12 text-center">
          <XCircle className="w-12 h-12 text-stone-300 mx-auto mb-3" />
          <p className="text-[#57534E]">Unable to load Stripe price data</p>
        </Card>
      ) : (
        <div className="space-y-4">
          {data.prices.map((row) => {
            const hasError = !!row.error;
            const isMatch = row.matches && !hasError;
            return (
              <Card
                key={row.plan_id}
                className={`p-5 border-2 ${
                  isMatch
                    ? 'border-emerald-200 bg-emerald-50/30'
                    : hasError
                      ? 'border-red-200 bg-red-50/40'
                      : 'border-amber-300 bg-amber-50/40'
                }`}
                data-testid={`price-row-${row.plan_id}`}
              >
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="flex items-center gap-3">
                    {isMatch ? (
                      <CheckCircle className="w-6 h-6 text-emerald-600 flex-shrink-0" />
                    ) : hasError ? (
                      <XCircle className="w-6 h-6 text-red-600 flex-shrink-0" />
                    ) : (
                      <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0" />
                    )}
                    <div>
                      <h3 className="font-bold text-[#2D2D2D] text-lg">{row.plan_name}</h3>
                      <p className="text-xs text-stone-500 font-mono">{row.env_var}</p>
                    </div>
                  </div>
                  <span
                    className={`text-xs font-semibold px-3 py-1 rounded-full whitespace-nowrap ${
                      isMatch
                        ? 'bg-emerald-100 text-emerald-700'
                        : hasError
                          ? 'bg-red-100 text-red-700'
                          : 'bg-amber-100 text-amber-700'
                    }`}
                  >
                    {isMatch ? 'Match' : hasError ? 'Error' : 'Mismatch'}
                  </span>
                </div>

                {hasError ? (
                  <div className="bg-white rounded-md p-3 text-sm text-red-700 font-mono">
                    {row.error}
                  </div>
                ) : (
                  <div className="grid md:grid-cols-2 gap-4">
                    {/* Local (Code) */}
                    <div className="bg-white rounded-md p-4 border border-stone-100">
                      <p className="text-xs font-semibold text-stone-500 uppercase tracking-wide mb-2">
                        Local Config (landing page)
                      </p>
                      <p className="text-2xl font-bold text-[#2D2D2D]">
                        {formatPrice(row.local_price, row.local_interval)}
                      </p>
                      <p className="text-xs text-stone-500 mt-1">
                        {row.local_interval || 'no interval set'}
                      </p>
                    </div>

                    {/* Stripe Live */}
                    <div className={`rounded-md p-4 border ${
                      isMatch ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'
                    }`}>
                      <p className="text-xs font-semibold text-stone-500 uppercase tracking-wide mb-2 flex items-center gap-1">
                        Stripe Live
                        <a
                          href={`https://dashboard.stripe.com/${data.stripe_mode === 'live' ? '' : 'test/'}prices/${row.stripe_price_id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-stone-400 hover:text-indigo-600"
                          title="Open in Stripe Dashboard"
                        >
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      </p>
                      <p className={`text-2xl font-bold ${isMatch ? 'text-emerald-700' : 'text-amber-700'}`}>
                        {formatPrice(row.stripe_amount, row.stripe_interval)}
                      </p>
                      <p className="text-xs text-stone-600 mt-1">
                        {row.stripe_interval || '—'}
                        {row.stripe_active === false && (
                          <span className="ml-2 text-red-600 font-semibold">INACTIVE</span>
                        )}
                        {row.stripe_product_name && (
                          <span className="block text-stone-500">
                            Product: {row.stripe_product_name}
                          </span>
                        )}
                      </p>
                    </div>
                  </div>
                )}

                {/* Price ID with copy */}
                <div className="mt-3 flex items-center justify-between gap-2 text-xs">
                  <span className="text-stone-500">
                    Stripe Price ID:{' '}
                    <code className="bg-stone-100 px-2 py-0.5 rounded text-stone-700 font-mono">
                      {row.stripe_price_id || '<not set>'}
                    </code>
                  </span>
                  {row.stripe_price_id && (
                    <button
                      onClick={() => copyToClipboard(row.stripe_price_id, 'Price ID')}
                      className="text-stone-400 hover:text-indigo-600"
                      data-testid={`copy-price-id-${row.plan_id}`}
                      title="Copy Price ID"
                    >
                      <Copy className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Help footer */}
      <Card className="p-5 bg-stone-50 border-stone-200">
        <h3 className="font-bold text-[#2D2D2D] mb-2">How to fix a mismatch</h3>
        <ol className="text-sm text-[#57534E] space-y-1 list-decimal list-inside">
          <li>
            Open <a href="https://dashboard.stripe.com/products" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline">Stripe Dashboard → Products</a>
          </li>
          <li>
            Find or create a new Price object with the desired amount (Stripe doesn't allow editing existing Price amounts — you must create a new one)
          </li>
          <li>
            Copy the new Price ID (starts with <code className="bg-stone-100 px-1 rounded">price_…</code>)
          </li>
          <li>
            In Emergent → Secrets, update the env variable shown above for that tier
          </li>
          <li>
            Redeploy (production) or restart backend (preview), then come back here and click Refresh
          </li>
          <li>
            Archive the old Price in Stripe so it can't accidentally be reused
          </li>
        </ol>
      </Card>
    </div>
  );
};

export default AdminStripePriceCheckPage;
